import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// R13 Workstream 4 - renders the backend's structured "a Case can't be created" decision
// (GET /cases/eligibility). It decides nothing itself: the reasons, next actions, existing Case and
// whether the signed-in person may take an exception all come straight from the server, so every surface
// that shows this refusal says the same thing.
//
// An exception is offered ONLY when the server says every blocking reason is exceptionable AND the person
// holds the explicit grant for each kind (an administrator's wildcard does not count). A reason alone is
// never enough: the evidence the exception rests on must be cited too.
export const MIN_EXCEPTION_REASON = 10;
export const MIN_EXCEPTION_EVIDENCE = 3;

const KIND_LABEL = {
  duplicate: "a second / reviewed Case",
  no_pursuit: "a Case with no active opportunity",
  prerequisites: "a Case before signup, payment and approval are complete",
  migration: "a historical migration",
};
const KIND_CAPABILITY = {
  duplicate: "case.exception.duplicate",
  no_pursuit: "case.exception.no_pursuit",
  prerequisites: "case.exception.prerequisites",
  migration: "case.exception.migration",
};

export const isExceptionable = (elig) => !!elig && !elig.allowed && !!elig.exceptionable && !!elig.can_use_exception;

// What the create request must carry for an exception (the server re-checks every one of these).
export const exceptionPayload = (elig, reason, evidenceRef) => {
  const kinds = elig?.exception_kinds_needed || [];
  const out = { reason: (reason || "").trim(), evidence_ref: (evidenceRef || "").trim() };
  if (kinds.includes("duplicate")) out.duplicate_exception = true;
  if (kinds.includes("no_pursuit") || kinds.includes("prerequisites")) out.override = true;
  return out;
};

export const exceptionReady = (useException, reason, evidenceRef) => !!useException
  && (reason || "").trim().length >= MIN_EXCEPTION_REASON && (evidenceRef || "").trim().length >= MIN_EXCEPTION_EVIDENCE;

export const CaseBlockedPanel = ({
  elig, onOpenCase, useException, setUseException, exceptionReason, setExceptionReason,
  evidenceRef, setEvidenceRef,
}) => {
  if (!elig || elig.allowed) return null;
  const existing = elig.existing_case || null;
  const kinds = elig.exception_kinds_needed || [];
  const offered = isExceptionable(elig);
  const needsGrant = !!elig.exceptionable && !elig.can_use_exception;
  return (
    <div data-testid="case-blocked-panel" role="alert"
      className="rounded border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/5 p-2.5 space-y-1.5 text-xs">
      <p className="font-medium text-[hsl(var(--danger))]">A Case can't be created yet</p>
      {(elig.reasons || []).map((r, i) => (
        <div key={i} data-testid={`case-blocked-reason-${r.code}`}>
          <p>{r.message}</p>
          {r.next_action && <p className="text-muted-foreground">Next: {r.next_action}</p>}
        </div>
      ))}
      {existing && (
        <Button type="button" size="sm" variant="outline" data-testid="case-open-existing"
          onClick={() => onOpenCase && onOpenCase(existing.case_id)}>
          Open Case {existing.case_id}
        </Button>
      )}
      {needsGrant && (
        <p data-testid="case-exception-needs-grant" className="text-muted-foreground">
          An exception is possible for this ({kinds.map((k) => KIND_LABEL[k] || k).join("; ")}) but it needs an explicit
          grant ({kinds.map((k) => KIND_CAPABILITY[k]).join(", ")}). Ask Leadership.
        </p>
      )}
      {offered && (
        <div className="pt-1 space-y-1">
          <label className="flex items-center gap-2">
            <input type="checkbox" data-testid="case-exception-toggle" checked={!!useException}
              onChange={(e) => setUseException(e.target.checked)} />
            <span>Take an audited exception: {kinds.map((k) => KIND_LABEL[k] || k).join("; ")}</span>
          </label>
          {useException && (
            <>
              <Input data-testid="case-exception-reason" placeholder="Why is this Case needed? (min 10 characters)"
                value={exceptionReason} onChange={(e) => setExceptionReason(e.target.value)} />
              <Input data-testid="case-exception-evidence" placeholder="Evidence reference - ticket, approval, legacy file or note id"
                value={evidenceRef || ""} onChange={(e) => setEvidenceRef && setEvidenceRef(e.target.value)} />
              <p className="text-muted-foreground">
                No payment, signup or approval is created by an exception, and no conversion credit or provider
                event is sent. Who, why and on what evidence is audited.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};
